/// <amd-module name="Favorites/Dropdown/ArtifactDropdownProvider" />
import { ArtifactScope, Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesCollection, IFavoritesService } from "Favorites/Common/FavoritesService";
import * as FavoriteResources from "Favorites/Common/Resources";
import * as React from "react";
import {
    IObservableArrayEventArgs,
    IReadonlyObservableValue,
    IReadyableReadonlyObservableArray,
    ObservableArrayAction,
    ObservableValue,
    ReadyableObservableArray
} from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { Button } from "VSSUI/Button";
import { IDropdownPivot } from "VSSUI/Dropdown";
import { IIconProps } from "VSSUI/Icon";
import { ListSelection, renderListCell } from "VSSUI/List";
import { IListBoxGroup, IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { css } from "VSSUI/Util";
import { AggregateItemProvider } from "VSSUI/Utilities/AggregateItemProvider";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { ClipboardButton } from "VSSUI/Clipboard";
import * as ClipboardResources from "VSSUI/Resources.Clipboard";

export interface IFavoritesContext {
    /**
     * Type of the favorited artifact (query, team, repository, etc.)
     */
    artifactType: string;

    /**
     * Describes the scope a favorited Artifact resides in. e.g. A team project
     */
    artifactScope: ArtifactScope;

    /**
     * Optional (already-fetched) collection of favorites
     */
    favorites?: IFavoritesCollection;
}

/**
 * Defines only the funtionality of a favorites service that this picker requires.
 */
export interface FavoritePickerDataService {
    /**
     * Whether the service supports to query favorites with extended details (probably slower).
     */
    canFetchExtendedDetails: () => boolean;

    /**
     * Add (favorite) the given item
     */
    addFavorite(favorite: Favorite): Promise<Favorite>;

    /**
     * Describes if the current user has general read and write permissions on the Favorites Service.
     */
    canUseFavorites(): boolean;

    /**
     * Get favorites of the given artifact type
     */
    getFavorites(
        artifactType: string,
        artifactScopeType?: string | undefined,
        artifactScopeId?: string | undefined,
        includeExtendedDetails?: boolean | undefined
    ): Promise<IFavoritesCollection>;

    /**
     * Remove (un-favorite) the given favorite item
     */
    removeFavorite(favorite: Favorite): Promise<void>;
}

export interface IArtifactDropdownProviderOptions<TArtifact> {
    /**
     * Header text for the default group
     */
    defaultGroupHeader?: string;

    /**
     * Header text for the favorites group
     */
    favoriteGroupHeader?: string;

    /**
     * Optional context for favorites to use. This will enable consumers to subscribe
     * favorite operation like toggle themselves as well as favorite picker.
     *
     * If not set, then favorites are not leveraged.
     */
    favoritesContext?: IFavoritesContext;

    /**
     * Optional service to use for data access in place of the standard favorites service.
     * Use it if your picker uses a non-standard favoriting layer (e.g. GitRefFavorite)
     */
    favoritesService?: FavoritePickerDataService;

    /**
     * Optional delegate to get the URL for the given item.
     */
    getArtifactHref?: (item: TArtifact) => string;

    /**
     * Optional delegate to convert a Favorite item to an artifact. If specified, all favorite items will be shown
     * in the dropdown immediately, even if the initial item fetch has not yet completed. Favorite items will be
     * fully resolved if/when a matching item is returned from getArtifacts.
     */
    getArtifactFromFavorite?: (favoriteItem: Favorite) => TArtifact;

    /**
     * Gets the icon to show to the left of the specified item.
     */
    getArtifactIcon?: (item: TArtifact) => IIconProps;

    /**
     * Get the artifactId from the given item (used for matching the artifact with a Favorite item and for the item's key)
     */
    getArtifactId: (item: TArtifact) => string;

    /**
     * Optional delegate to get a pick list group id for a given artifact.
     */
    getArtifactListGroupId?: (item: TArtifact) => string;

    /**
     * Get the name to display as text for the given artifact in the pick list
     */
    getArtifactName: (item: TArtifact) => string;

    /**
     * Delegate to fetch the set of artifacts to show in the picker.
     * @param groupKey If a group needs an extra fetch (isLoading = true), this call retrieves these additional items.
     * New items will be joined with previous ones.
     */
    getArtifacts: (groupKey?: string) => IReadyableReadonlyObservableArray<TArtifact>;

    /**
     * Optional delegate to get the textNode for an item if needs to be something more complex than a string.
     */
    getArtifactTextNode?: (item: IListBoxItem<TArtifact>) => JSX.Element | undefined;

    /**
     * Optional delegate to create a favorite entry from the given artifact. If specified, artifacts that
     * don't yet exist in the artifact store can still be favorited.
     */
    getFavoriteFromArtifact?: (artifact: TArtifact) => Favorite | undefined;

    /**
     * Groups of items to render.
     */
    groups?: IListBoxGroup[];

    /**
     * A mapping of group ids to pivot ids.
     */
    groupToPivotMap?: { [groupId: string]: string };

    /**
     * Optional delegate to be called when an error is encountered by favoriting/unfavoriting an item
     * in the picker.
     */
    onFavoriteLoadError?: (error: any) => void;

    /**
     * Optional callback to get notified when favorties have loaded.
     */
    onFavoritesLoaded?: () => void;

    /**
     * The current page context
     */
    pageContext: IVssPageContext;

    /**
     * the pivots to render.
     */
    pivots?: IDropdownPivot<TArtifact>[];

    /**
     * If true, don't show the favorite (star) indicator next to the selected item.
     */
    hideFavoriteSelectedItemIndicator?: boolean;
}

export interface IArtifactDropdownProvider {
    /**
     * Disposes all resources associated with the provider
     */
    dispose: () => void;
}

const DefaultPivotId = "default-pivot";

/**
 * Provides items for a picker which shows artifacts with favorites support.
 */
export class ArtifactDropdownProviderBase<TArtifact = {}> implements IArtifactDropdownProvider, IItemProvider<IListBoxItem<TArtifact>> {

    /**
     * Get the length of the resolved listItems array.
     */
    public get length(): number {
        return this.aggregateProvider.length;
    }

    /**
     * Get the interal array of resolved listItems.
     */
    public get value(): IListBoxItem<TArtifact>[] {
        return this.aggregateProvider.value;
    }

    private options: IArtifactDropdownProviderOptions<TArtifact>;

    private artifacts: IReadyableReadonlyObservableArray<TArtifact> | undefined;
    private observers: { observable: IReadonlyObservableValue<boolean>; observer: (value: boolean) => void }[] = [];
    private artifactsLoaded: boolean;
    private resolvedArtifacts: IListBoxItem<TArtifact>[] | undefined;
    private resolvedArtifactsByPivot: { [pivotId: string]: IReadyableReadonlyObservableArray<IListBoxItem<TArtifact>> } = {};
    private pivotArtifactsById: { [pivotId: string]: { [artifactId: string]: TArtifact } } = {};
    private disposed = false;
    private favoritesService: FavoritePickerDataService;
    private favoritesCollection: IFavoritesCollection | undefined;
    private favoritesLoaded: boolean;
    private favoriteGroup: IListBoxGroup;
    private builtInGroups: IListBoxGroup[] = [];
    private defaultGroup: IListBoxGroup;
    private favoritesByArtifactId: { [artifactId: string]: ObservableValue<Favorite | undefined> } = {};
    private initialFavoriteItems: { [id: string]: boolean } = {};
    private extendedFavoritesLoaded = false;
    private groupedProvider = new GroupedItemProvider<TArtifact>([], [], false);
    private aggregateProvider: AggregateItemProvider<IListBoxItem<TArtifact>>;

    constructor(options?: IArtifactDropdownProviderOptions<TArtifact>) {
        if (options) {
            this.initialize(options);
        }
    }

    /**
     * Subscribe to changes in the underlying set of items.
     * @param observer the delegate to be called when there are updates.
     * @param action the action on the set to observe.
     */
    public subscribe(
        observer: (value: IObservableArrayEventArgs<IListBoxItem<TArtifact>>, action?: ObservableArrayAction) => void,
        action?: ObservableArrayAction
    ): (value: IObservableArrayEventArgs<IListBoxItem<TArtifact>>, action?: ObservableArrayAction) => void {
        return this.aggregateProvider.subscribe(observer, action);
    }

    /**
     * Unsubscribe from changes in the underlying set of items.
     * @param observer the delegate that was used to subscribe.
     * @param action the action that was used to subsribe.
     */
    public unsubscribe(
        observer: (value: IObservableArrayEventArgs<IListBoxItem<TArtifact>>, action?: ObservableArrayAction) => void,
        action?: ObservableArrayAction
    ): void {
        return this.aggregateProvider.unsubscribe(observer, action);
    }

    public dispose(): void {
        if (this.favoritesCollection) {
            if (this.options.favoritesContext!.favorites === this.favoritesCollection) {
                this.favoritesCollection.dispose();
            }
            this.favoritesCollection = undefined;
        }

        this.observers.forEach((observer) => {
            observer.observable.unsubscribe(observer.observer);
        });

        this.disposed = true;
    }

    /**
     * Get list of items to be displayed in favorites section
     */
    public getItems = () => {
        const userIsMember: boolean = userHasClaim(this.options.pageContext, UserClaims.Member);

        if (!this.artifacts) {
            this.artifacts = this.options.getArtifacts();

            const process = (artifacts: TArtifact[]) => {
                this.resolvedArtifacts = this.processResolvedArtifacts(artifacts);
                this.artifactsLoaded = true;
                // Still load favorites for anonymous access users.
                if (!userIsMember) {
                    this.favoritesLoaded = true;
                }
                // Don't add until favorites have loaded to prevent items jumping around.
                if (this.favoritesLoaded) {
                    this.addResolvedItems(this.resolvedArtifacts);
                }
            };

            if (!this.artifacts.ready.value) {
                const artifactsReadyObserver = (value: boolean) => {
                    if (value && this.artifacts) {
                        process(this.artifacts.value);
                    }
                };
                this.addObserver(this.artifacts.ready, artifactsReadyObserver);
            } else {
                process(this.artifacts.value);
            }
        }

        if (!this.options.favoritesContext || !userIsMember) {
            // Not using favorites or user is not logged in, just return
            return;
        }

        // takes care of deleted items, name/scope updates for the picker text and on the dropdown being opened.
        this.loadFavorites();

        if (!this.favoritesCollection) {
            // Favorites not loaded yet. Don't return items until we at least have favorites since
            // we are going to display the favorites as the first group.
            return;
        }

        // Sort favorites and reset the lookup
        const favorites: Favorite[] = this.favoritesCollection.value.filter((f) => !f.artifactIsDeleted);
        this.initialFavoriteItems = {};

        // Populate the favorite item lookup by artifact id
        for (const favorite of favorites) {
            if (this.favoritesByArtifactId[favorite.artifactId]) {
                this.favoritesByArtifactId[favorite.artifactId].value = favorite;
            } else {
                this.favoritesByArtifactId[favorite.artifactId] = new ObservableValue<Favorite>(favorite);
            }
        }
    };

    /**
     * Returns a ListBoxItem for the specified item
     */
    public getListItem = (item: TArtifact): IListBoxItem<TArtifact> => {
        const {
            getArtifactHref,
            getArtifactName,
            getArtifactId,
            getArtifactListGroupId,
            getArtifactIcon,
            getFavoriteFromArtifact,
            getArtifactTextNode
        } = this.options;
        let useTableItemTooltip = true;
        const listItem: IListBoxItem<TArtifact> = {
            data: item,
            text: getArtifactName(item),
            id: getArtifactId(item)
        };

        if (this.getFavoriteItem(getArtifactId(item))) {
            listItem.groupId = "favorites";
        } else if (getArtifactListGroupId) {
            listItem.groupId = getArtifactListGroupId(item);
        }
        if (!listItem.groupId) {
            listItem.groupId = "default";
        }

        if (getArtifactIcon) {
            listItem.iconProps = getArtifactIcon(item);
        }

        if (!this.favoritesByArtifactId[listItem.id]) {
            this.favoritesByArtifactId[listItem.id] = new ObservableValue<Favorite | undefined>(undefined);
        }

        listItem.render = (
            rowIndex: number,
            columnIndex: number,
            tableColumn: ITableColumn<IListBoxItem<TArtifact>>,
            tableItem: IListBoxItem<TArtifact>
        ) => {
            let showFavoriteIcon = !this.options.hideFavoriteSelectedItemIndicator;
            if (!listItem.id) {
                showFavoriteIcon = false;
            } else {
                const favoriteItem = this.getFavoriteItem(listItem.id);
                if (!favoriteItem && (!getFavoriteFromArtifact || !getFavoriteFromArtifact(item))) {
                    // Not in the favorite store, and we don't have the ability to create a favorite
                    // from this artifact, so don't show an indicator.
                    showFavoriteIcon = false;
                }
            }
            if (!tableItem.tooltipProps) {
                tableItem.tooltipProps = { disabled: true };
                useTableItemTooltip = false;
            }
            const favoritesContext = this.options.favoritesContext!;            
            const isBranch =  favoritesContext.artifactType.toString() === "Microsoft.TeamFoundation.Git.GitRefFavorite" ? true : false;
            return (
                <SimpleTableCell
                    className={css(
                        tableColumn.className,
                        tableItem.className,
                        tableItem.type === ListBoxItemType.Header && "bolt-list-box-header",
                        "artifact-list-item"
                    )}
                    columnIndex={columnIndex}
                    key={columnIndex}
                    tableColumn={tableColumn}
                >
                    {tableItem &&
                        renderListCell(
                            {
                                ...tableItem,
                                textNode: getArtifactTextNode ? getArtifactTextNode(listItem) : undefined,
                                href: getArtifactHref && listItem.data ? getArtifactHref(listItem.data) : undefined,
                                textClassName: "flex-grow artifact-link no-underline-link"
                            },
                            !useTableItemTooltip
                        )}
                    { isBranch &&
                        <ClipboardButton
                            className="artifact-dropdown-branch-copy-button"
                            getContent={() => tableItem.text?.toString() ?? ''}
                            key="clipboard"
                            showCopiedTooltip={true}
                            tooltipProps={{ text: ClipboardResources.CopyToClipboard }}
                        />
                    }

                    {showFavoriteIcon && (
                        <Observer favorite={this.favoritesByArtifactId[listItem.id]}>
                            {(props: { favorite: Favorite | undefined }) => {
                                const text = props.favorite
                                    ? FavoriteResources.FavoriteStarFavoritedTitle
                                    : FavoriteResources.FavoriteStarUnfavoritedTitle;

                                return (
                                    <Button
                                        ariaLabel={text}
                                        className="artifact-item-favorite-button"
                                        iconProps={{
                                            iconName: !!props.favorite ? "FavoriteStarFill" : "FavoriteStar",
                                            className: css(
                                                "artifact-item-favorite-icon ms-font-l",
                                                (!!props.favorite || this.initialFavoriteItems[listItem.id]) && "show-favorite"
                                            )
                                        }}
                                        subtle={true}
                                        tooltipProps={{ text }}
                                        onClick={(event) => this.onFavoriteClick(event, tableItem)}
                                    />
                                );
                            }}
                        </Observer>
                    )}
                </SimpleTableCell>
            );
        };

        return listItem;
    };

    protected addObserver = (observable: IReadonlyObservableValue<boolean>, observer: (value: boolean) => void) => {
        observable.subscribe(observer);
        this.observers.push({ observable, observer });
    };

    protected initialize(options: IArtifactDropdownProviderOptions<TArtifact>) {
        this.options = options;
        this.aggregateProvider = new AggregateItemProvider<IListBoxItem<TArtifact>>();
        if (this.options.pivots) {
            this.options.pivots.forEach((pivot: IDropdownPivot<TArtifact>) => {
                this.aggregateProvider.push(pivot.items);
            });
            this.favoritesService = options.favoritesService || options.pageContext.getService<IFavoritesService>("IFavoritesService");
        } else {
            this.aggregateProvider.push(this.groupedProvider);

            this.initializeDefaultGroup();

            const favoritesContext = options.favoritesContext;
            this.favoritesService = options.favoritesService || options.pageContext.getService<IFavoritesService>("IFavoritesService");
            if (favoritesContext && this.favoritesService.canUseFavorites()) {
                this.initializeFavoriteGroup();

                if (favoritesContext.favorites) {
                    this.onFavoritesLoaded(favoritesContext.favorites);
                }
            }
        }
    }

    protected forgetCachedArtifacts = (pivotId?: string): void => {
        if (pivotId) {
            this.pivotArtifactsById[pivotId] = {};
        } else {
            this.artifacts = undefined;
            this.pivotArtifactsById = {};
            this.resolvedArtifacts = undefined;
        }
    };

    protected loadFavorites(): Promise<IFavoritesCollection | undefined> {
        if (this.extendedFavoritesLoaded) {
            return Promise.resolve(undefined);
        }

        const favoritesContext = this.options.favoritesContext!;
        const promise = this.favoritesService
            .getFavorites(favoritesContext.artifactType, favoritesContext.artifactScope.type, favoritesContext.artifactScope.id, true)
            .then(this.onFavoritesLoaded, this.favoritesErrorDelegate);

        this.extendedFavoritesLoaded = true;

        return promise;
    }

    protected onGroupLoadRequest = (groupKey: string, lastCallItemsCount: number = 0): IReadyableReadonlyObservableArray<IListBoxItem<TArtifact>> => {
        if (groupKey === "favorites" || groupKey === "default") {
            const results = new ReadyableObservableArray<IListBoxItem<TArtifact>>();
            results.ready.value = true;
            return results;
        }

        const artifacts = this.options.getArtifacts(groupKey);
        const pivotId = (this.options.groupToPivotMap && this.options.groupToPivotMap[groupKey]) || "";
        const resolvedArtifacts = new ReadyableObservableArray<IListBoxItem<TArtifact>>();
        this.resolvedArtifactsByPivot[pivotId] = resolvedArtifacts;

        const process = (artifacts: TArtifact[]) => {
            const previousCount = this.resolvedArtifactsByPivot[pivotId] && this.resolvedArtifactsByPivot[pivotId].length;
            resolvedArtifacts.push(...this.processResolvedArtifacts(artifacts, pivotId));
            resolvedArtifacts.ready.value = true;
            if (
                artifacts.length > lastCallItemsCount &&
                this.resolvedArtifactsByPivot[pivotId].length === previousCount &&
                this.options.groups &&
                this.options.groups.some((g) => g.name === groupKey && Boolean(g.loading))
            ) {
                // We didn't load anything new, and given that the group is not still fully loaded,
                // we assume that every retrieved item was already in another group, so let's load another page.
                // Only while fetched items are strictly greater, which prevents infinite recursion.
                return this.onGroupLoadRequest(groupKey, artifacts.length);
            }

            return this.resolvedArtifactsByPivot[pivotId];
        };

        if (!artifacts.ready.value) {
            const observer = (value: boolean) => {
                if (value) {
                    process(artifacts.value);
                }
            };
            this.addObserver(artifacts.ready, observer);
        } else {
            process(artifacts.value);
        }

        return resolvedArtifacts;
    };

    protected processResolvedArtifacts(artifacts: TArtifact[], pivotId?: string): IListBoxItem<TArtifact>[] {
        const pivot = pivotId || DefaultPivotId;
        const newArtifacts = [];
        if (!this.pivotArtifactsById[pivot]) {
            this.pivotArtifactsById[pivot] = {};
        }
        // Build hash of resolved artifacts by artifact id so we can determine which items are new.
        for (const artifact of artifacts) {
            const artifactId = this.options.getArtifactId(artifact);
            if (!this.pivotArtifactsById[pivot][artifactId]) {
                newArtifacts.push(artifact);
            }
            this.pivotArtifactsById[pivot][artifactId] = artifact;
        }

        return newArtifacts.map((artifact) => this.getListItem(artifact));
    }

    private addResolvedItems = (resolvedItems?: IListBoxItem<TArtifact>[]) => {
        if (this.favoritesLoaded && this.artifactsLoaded && this.defaultGroup) {
            this.groupedProvider.setGroupLoading(this.defaultGroup.id, false);
        }
        if (resolvedItems) {
            this.groupedProvider.push(...resolvedItems);
        }
    };

    private onFavoriteClick = (
        event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>,
        listItem: IListBoxItem<TArtifact>
    ) => {
        // Get/create the favorite item
        let favorited = false;
        let clickedFavoriteItem: Favorite | undefined = this.getFavoriteItem(listItem.id);
        if (clickedFavoriteItem) {
            favorited = true;
        } else {
            clickedFavoriteItem = this.options.getFavoriteFromArtifact!(listItem.data!);
            if (!clickedFavoriteItem) {
                return;
            }
        }

        // perform actual toggle through favorite service
        if (favorited) {
            this.favoritesService.removeFavorite(clickedFavoriteItem);
        } else {
            this.favoritesService.addFavorite(clickedFavoriteItem);
        }

        event.preventDefault();
    };

    private initializeDefaultGroup() {
        const builtInDefaultGroup = {
            id: "default",
            name: this.options.defaultGroupHeader,
            loading: true
        };

        this.artifactsLoaded = false;

        // provided group is only supported in synchronous mode
        const providedGroup = this.tryGetGroupSynchronously("default");
        if (providedGroup) {
            this.defaultGroup = providedGroup;
        } else {
            this.defaultGroup = builtInDefaultGroup;
            this.builtInGroups.push(builtInDefaultGroup);
            this.groupedProvider.pushGroups(builtInDefaultGroup);
        }
    }

    private initializeFavoriteGroup() {
        const builtInFavoritesGroup: IListBoxGroup<TArtifact> = {
            id: "favorites",
            name: this.options.favoriteGroupHeader
        };
        this.favoritesLoaded = false;

        // provided favorites group is only supported in synchronous mode
        const providedGroup = this.tryGetGroupSynchronously("favorites");
        if (providedGroup) {
            this.favoriteGroup = providedGroup;
        } else {
            this.favoriteGroup = builtInFavoritesGroup;
            this.builtInGroups.unshift(builtInFavoritesGroup);
            this.groupedProvider.spliceGroups(0, 0, builtInFavoritesGroup);
        }
    }

    private tryGetGroupSynchronously(groupKey: string): IListBoxGroup | undefined {
        const { groups } = this.options;
        if (groups && !isPromise(groups)) {
            return groups.filter((g) => g.id === groupKey)[0];
        }
    }

    private favoritesErrorDelegate = (error: any): undefined => {
        if (this.options.onFavoriteLoadError) {
            this.options.onFavoriteLoadError(error);
        }

        this.favoritesLoaded = true;
        this.addResolvedItems(this.resolvedArtifacts);

        return undefined;
    };

    private getFavoriteItem(artifactId: string): Favorite | undefined {
        const favoriteItem = this.favoritesByArtifactId[artifactId];
        if (!favoriteItem && this.favoritesCollection && !this.artifacts) {
            // We haven't gone through getItems yet, but the favorites collection is available. Check there.
            return this.favoritesCollection.value.filter((favorite) => favorite.artifactId === artifactId)[0];
        }
        return favoriteItem ? favoriteItem.value : favoriteItem;
    }

    private onFavoritesLoaded = (collection: IFavoritesCollection): IFavoritesCollection | undefined => {
        if (this.disposed) {
            // Already disposed when the favorites-fetch completed
            collection.dispose();
            return;
        } else if (this.favoritesCollection && this.favoritesCollection !== this.options.favoritesContext!.favorites) {
            // Dispose the previous collection
            this.favoritesCollection.dispose();
        }
        const sortedValues = [...collection.value].sort((favorite1, favorite2) => {
            return localeIgnoreCaseComparer(favorite1.artifactName, favorite2.artifactName);
        });
        this.favoritesCollection = collection;
        collection.subscribe(this.onFavoriteItemChanged);

        sortedValues.forEach((favorite) => {
            if (favorite.artifactIsDeleted) {
                return;
            }
            this.initialFavoriteItems[favorite.artifactId] = true;
            if (this.favoritesByArtifactId[favorite.artifactId]) {
                this.favoritesByArtifactId[favorite.artifactId].value = favorite;
            } else {
                this.favoritesByArtifactId[favorite.artifactId] = new ObservableValue<Favorite>(favorite);
            }
            if (this.resolvedArtifacts) {
                const index = this.resolvedArtifacts.findIndex((item) => item.id === favorite.artifactId);
                if (index > -1) {
                    this.resolvedArtifacts.splice(index, 1, { ...this.resolvedArtifacts[index], groupId: this.favoriteGroup.id });
                }
            }
        });

        if (this.options.onFavoritesLoaded) {
            this.options.onFavoritesLoaded();
        }

        this.favoritesLoaded = true;
        if (this.options.getArtifactFromFavorite) {
            const favoriteArtifacts: TArtifact[] = [];
            sortedValues.forEach((favorite) => {
                if (!favorite.artifactIsDeleted) {
                    favoriteArtifacts.push(this.options.getArtifactFromFavorite!(favorite));
                }
            });
            const newArtifacts = this.processResolvedArtifacts(favoriteArtifacts);
            this.addResolvedItems(newArtifacts);
        }

        if (this.resolvedArtifacts) {
            this.addResolvedItems(this.resolvedArtifacts);
        }

        return collection;
    };

    private onFavoriteItemChanged = (ev: IObservableArrayEventArgs<Favorite>, action?: string): void => {
        if (ev.addedItems) {
            for (const item of ev.addedItems) {
                this.favoritesByArtifactId[item.artifactId].value = item;
            }
        }
        if (ev.removedItems) {
            for (const item of ev.removedItems) {
                this.favoritesByArtifactId[item.artifactId].value = undefined;
            }
        }
    };
}

/**
 * Provides items for a picker which shows artifacts with favorites support.
 */
export class ArtifactDropdownProvider<TArtifact = {}> extends ArtifactDropdownProviderBase<TArtifact> {
    constructor(options: IArtifactDropdownProviderOptions<TArtifact>) {
        super(options);
    }
}

function isPromise<T>(choice: T | Promise<T>): choice is Promise<T> {
    return typeof (choice as Promise<T>).then === "function";
}
